import { onBeforeUnmount } from 'vue';

import { eventBus } from '@/eventbus/eventbus';

type EventHandler = (payload?: any) => void;

export interface IUseEventBus {
  on: (event: string, handler: EventHandler) => void;
  off: (event: string, handler: EventHandler) => void;
  emit: (event: string, payload?: unknown) => void;
}

/**
 * The app event bus (`$eventBus`) for `<script setup>`. Listeners added with `on` are
 * removed when the component unmounts, so there is no `$off` to forget.
 */
export const useEventBus = (): IUseEventBus => {
  const listeners: Array<[string, EventHandler]> = [];

  const on = (event: string, handler: EventHandler): void => {
    eventBus.$on(event, handler);
    listeners.push([event, handler]);
  };

  const off = (event: string, handler: EventHandler): void => {
    eventBus.$off(event, handler);
    const index = listeners.findIndex(([listenerEvent, listenerHandler]) => listenerEvent === event && listenerHandler === handler);

    if (index !== -1) {
      listeners.splice(index, 1);
    }
  };

  const emit = (event: string, payload?: unknown): void => {
    eventBus.$emit(event, payload);
  };

  onBeforeUnmount(() => {
    listeners.forEach(([event, handler]) => eventBus.$off(event, handler));
  });

  return { on, off, emit };
};
